/**
 * Stickman - 火柴人角色主类
 * @description 整合物理骨骼和渲染器，实现状态机和拖拽交互
 */

import Phaser from 'phaser';
import planck, { Vec2 } from 'planck';
import { PhysicsWorld } from '@/physics/PhysicsWorld.ts';
import { StickmanBody } from './StickmanBody.ts';
import { StickmanRenderer } from './StickmanRenderer.ts';
import type { StickmanColors } from './StickmanRenderer.ts';

/** 火柴人动作状态 */
export type StickmanState = 'standing' | 'walking' | 'jumping' | 'dragging';

/** 火柴人配置 */
export interface StickmanConfig {
  /** 初始位置 x */
  x: number;
  /** 初始位置 y */
  y: number;
  /** 缩放因子 */
  scale?: number;
  /** 颜色配置 */
  colors?: StickmanColors;
  /** 行走速度 */
  walkSpeed?: number;
  /** 跳跃力度 */
  jumpForce?: number;
}

/** 默认配置 */
const DEFAULT_STICKMAN_CONFIG: Required<Omit<StickmanConfig, 'colors'>> = {
  x: 400,
  y: 300,
  scale: 1,
  walkSpeed: 5,
  jumpForce: 200,
};

/**
 * Stickman 类
 * 火柴人角色主类，管理物理、渲染、状态和交互
 */
export class Stickman {
  private scene: Phaser.Scene;
  private physicsWorld: PhysicsWorld;
  private body: StickmanBody;
  private renderer: StickmanRenderer;
  private state: StickmanState = 'standing';
  private config: Required<StickmanConfig>;
  private mouseJoint: planck.MouseJoint | null = null;
  private offsetY: number;
  private facingDirection: number = 1;

  /**
   * 创建火柴人角色
   * @param scene Phaser 场景
   * @param physicsWorld 物理世界
   * @param config 配置选项
   * @param offsetY y 轴偏移（屏幕高度），默认 600
   */
  constructor(
    scene: Phaser.Scene,
    physicsWorld: PhysicsWorld,
    config: StickmanConfig,
    offsetY: number = 600
  ) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;
    this.config = {
      ...DEFAULT_STICKMAN_CONFIG,
      ...config,
      colors: config.colors ?? {
        line: 0xffffff,
        headFill: 0x1a1a2e,
        lineWidth: 3,
      },
    };
    this.offsetY = offsetY;

    const position = Vec2(this.config.x, offsetY - this.config.y);

    this.body = new StickmanBody(physicsWorld, position, this.config.scale);
    this.renderer = new StickmanRenderer(
      scene,
      this.body,
      this.config.colors,
      this.config.scale,
      offsetY
    );

    this.setupInteraction();
  }

  /**
   * 设置交互（鼠标/触屏拖拽）
   */
  private setupInteraction(): void {
    const graphics = this.renderer.getGraphics();

    graphics.setInteractive(
      new Phaser.Geom.Circle(0, 0, 100),
      Phaser.Geom.Circle.Contains
    );

    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.isPointerNearHead(pointer)) {
        this.startDragging(pointer);
      }
    });

    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.mouseJoint && this.state === 'dragging') {
        this.updateDragPosition(pointer);
      }
    });

    this.scene.input.on('pointerup', () => {
      this.stopDragging();
    });
  }

  /**
   * 检测指针是否靠近头部
   */
  private isPointerNearHead(pointer: Phaser.Input.Pointer): boolean {
    const headScreenPos = this.renderer.getHeadScreenPosition();
    const headRadius = 15 * this.config.scale * 2;
    const distance = Math.sqrt(
      Math.pow(pointer.x - headScreenPos.x, 2) +
      Math.pow(pointer.y - headScreenPos.y, 2)
    );
    return distance < headRadius;
  }

  /**
   * 开始拖拽
   */
  private startDragging(pointer: Phaser.Input.Pointer): void {
    this.setState('dragging');
    this.body.wakeUpAll();

    const world = this.physicsWorld.getWorld();
    const headBody = this.body.getBody('head');

    if (!headBody) return;

    const targetPos = Vec2(pointer.x, this.offsetY - pointer.y);

    const mouseJointDef = planck.MouseJoint({
      bodyA: this.getGroundBody(),
      bodyB: headBody,
      target: targetPos,
      maxForce: 1000,
    });

    this.mouseJoint = world.createJoint(mouseJointDef) as planck.MouseJoint;
  }

  /**
   * 更新拖拽位置
   */
  private updateDragPosition(pointer: Phaser.Input.Pointer): void {
    if (!this.mouseJoint) return;

    const targetPos = Vec2(pointer.x, this.offsetY - pointer.y);
    this.mouseJoint.setTarget(targetPos);
  }

  /**
   * 停止拖拽
   */
  private stopDragging(): void {
    if (this.mouseJoint) {
      this.physicsWorld.getWorld().destroyJoint(this.mouseJoint);
      this.mouseJoint = null;
    }

    if (this.state === 'dragging') {
      this.setState('standing');
    }
  }

  /**
   * 获取地面物体（用于 MouseJoint）
   */
  private getGroundBody(): planck.Body {
    const ground = this.physicsWorld.getBody('ground');
    if (ground) return ground;

    const world = this.physicsWorld.getWorld();
    return world.createBody({ type: 'static' });
  }

  /**
   * 更新火柴人（每帧调用）
   */
  update(): void {
    this.renderer.render();

    if (this.state !== 'dragging') {
      this.updateState();
    }
  }

  /**
   * 根据物理状态更新动作状态
   */
  private updateState(): void {
    const torso = this.body.getBody('torso');
    if (!torso) return;

    const velocity = torso.getLinearVelocity();
    const speed = Math.abs(velocity.x);

    if (velocity.y > 1) {
      this.setState('jumping');
    } else if (speed > 0.5) {
      this.setState('walking');
      this.facingDirection = velocity.x > 0 ? 1 : -1;
    } else {
      this.setState('standing');
    }
  }

  /**
   * 设置状态
   */
  private setState(newState: StickmanState): void {
    if (this.state !== newState) {
      this.state = newState;
    }
  }

  /**
   * 获取当前状态
   */
  getState(): StickmanState {
    return this.state;
  }

  /**
   * 行走控制
   * @param direction 方向：1 为右，-1 为左，0 为停止
   */
  walk(direction: number): void {
    if (this.state === 'dragging') return;

    this.body.applyForceToTorso(
      direction * this.config.walkSpeed * 10,
      0
    );

    if (direction !== 0) {
      this.facingDirection = direction;
    }
  }

  /**
   * 跳跃
   */
  jump(): void {
    if (this.state === 'dragging' || this.state === 'jumping') return;

    this.body.applyForceToTorso(0, this.config.jumpForce);
    this.setState('jumping');
  }

  /**
   * 站立
   */
  stand(): void {
    this.body.setTorsoVelocity(0, 0);
    this.setState('standing');
  }

  /**
   * 获取躯干位置（屏幕坐标）
   */
  getPosition(): { x: number; y: number } {
    const torsoPos = this.body.getTorsoPosition();
    return {
      x: torsoPos.x * this.config.scale,
      y: this.offsetY - torsoPos.y * this.config.scale,
    };
  }

  /**
   * 获取头部位置（屏幕坐标）
   */
  getHeadPosition(): { x: number; y: number } {
    return this.renderer.getHeadScreenPosition();
  }

  /**
   * 设置颜色
   */
  setColors(colors: StickmanColors): void {
    this.renderer.setColors(colors);
  }

  /**
   * 销毁火柴人
   */
  destroy(): void {
    this.stopDragging();
    this.renderer.destroy();
    this.body.destroy();
  }

  /**
   * 获取物理骨骼实例
   */
  getBody(): StickmanBody {
    return this.body;
  }

  /**
   * 获取渲染器实例
   */
  getRenderer(): StickmanRenderer {
    return this.renderer;
  }

  /**
   * 获取朝向方向
   */
  getFacingDirection(): number {
    return this.facingDirection;
  }
}